import {
  buildWorkspacePath,
  createAdminWorkspaceTab,
  createProjectWorkspaceTab,
  makeProjectTabId,
  matchWorkspaceLocation,
  sanitizeWorkspaceSearchParams,
  type TabMemory,
  type TabSearchParams,
  type WorkspaceKind,
  type WorkspaceTab,
} from "./locations";
import {
  closeTabInState,
  mergeTabSearchParams,
  normalizeTabPath,
  openTabInState,
  replaceTabSearchParamsInState,
  updateTabMemoryInState,
  updateTabPathInState,
  updateTabSearchParamsInState,
} from "./tabs-reducer";

export type CloseTabResult = "closed" | "cancelled" | "not-found";
export type TabNavigationOptions = { replace?: boolean };
export type GuardedTabNavigationOptions = TabNavigationOptions & { skipGuard?: boolean };

export interface TabNavigationPolicyInput {
  path: string;
  currentSearchParams: TabSearchParams;
  requestedSearchParams: TabSearchParams | undefined;
  memory: TabMemory;
}

export interface TabNavigationPolicyResult {
  path: string;
  searchParams: TabSearchParams;
  memory: TabMemory;
}

export type TabNavigationPolicy = (input: TabNavigationPolicyInput) => TabNavigationPolicyResult;

export interface NavigationGuardRequest {
  type: "navigate" | "close-tab";
  /** Guards resume or cancel the store action; pendingPath was removed with one-way URL flow. */
  proceed: () => void;
  cancel: () => void;
}

export type NavigationGuardCallback = (request: NavigationGuardRequest) => void;

export interface TabsStoreDeps {
  commitUrl: (args: { to: string; search: TabSearchParams; replace?: boolean }) => void;
  initialLocation?: { pathname: string; search: TabSearchParams };
  loadTabs: () => WorkspaceTab[];
  saveTabs: (tabs: WorkspaceTab[]) => void;
}

export interface TabsSnapshot {
  tabs: WorkspaceTab[];
  activeTabId: string | null;
  activeWorkspaceKind: WorkspaceKind | null;
  isHome: boolean;
}

function areSearchParamsEqual(a: TabSearchParams, b: TabSearchParams): boolean {
  const aEntries = Object.entries(a);
  const bEntries = Object.entries(b);

  if (aEntries.length !== bEntries.length) {
    return false;
  }

  return aEntries.every(([key, value]) => b[key] === value);
}

function areTabMemoryEqual(a: TabMemory, b: TabMemory): boolean {
  return (
    a.entityPath === b.entityPath &&
    a.entityIconKey === b.entityIconKey &&
    a.assistantPath === b.assistantPath &&
    a.assistantDraftMarkdown === b.assistantDraftMarkdown
  );
}

function applyDefaultNavigationPolicy(
  kind: WorkspaceKind,
  input: TabNavigationPolicyInput
): TabNavigationPolicyResult {
  return {
    path: input.path,
    searchParams: mergeTabSearchParams(
      sanitizeWorkspaceSearchParams(kind, input.currentSearchParams).searchParams,
      sanitizeWorkspaceSearchParams(kind, input.requestedSearchParams ?? {}, {
        preserveUndefined: true,
      }).searchParams
    ),
    memory: input.memory,
  };
}

function mergeTabMemory(current: TabMemory, updates: Partial<TabMemory>): TabMemory {
  const merged: TabMemory = { ...current };

  for (const [key, value] of Object.entries(updates) as Array<
    [keyof TabMemory, string | undefined]
  >) {
    if (value === undefined) {
      delete merged[key];
      continue;
    }
    merged[key] = value;
  }

  return merged;
}

export function createTabsStore(deps: TabsStoreDeps) {
  let tabs: WorkspaceTab[] = deps.loadTabs();
  let pathname = deps.initialLocation?.pathname ?? "/";
  let search: TabSearchParams = deps.initialLocation?.search ?? {};
  let organizationId: string | null = null;
  const listeners = new Set<() => void>();
  const guards = new Map<string, NavigationGuardCallback>();
  const policies = new Map<string, TabNavigationPolicy>();

  function computeSnapshot(): TabsSnapshot {
    const activeWorkspaceMatch = matchWorkspaceLocation(pathname);
    return {
      tabs,
      activeTabId: activeWorkspaceMatch?.tabId ?? null,
      activeWorkspaceKind: activeWorkspaceMatch?.kind ?? null,
      isHome: pathname === "/",
    };
  }

  let snapshot: TabsSnapshot = computeSnapshot();

  function emit() {
    snapshot = computeSnapshot();
    for (const listener of listeners) {
      listener();
    }
  }

  function setTabs(nextTabs: WorkspaceTab[]) {
    if (nextTabs === tabs) return;
    tabs = nextTabs;
    deps.saveTabs(tabs);
    emit();
  }

  function setLocation(nextPathname: string, nextSearch: TabSearchParams) {
    const locationChanged = pathname !== nextPathname || !areSearchParamsEqual(search, nextSearch);
    pathname = nextPathname;
    search = nextSearch;
    if (locationChanged) emit();
  }

  function shouldCommitLocation(to: string, nextSearch: TabSearchParams): boolean {
    return pathname !== to || !areSearchParamsEqual(search, nextSearch);
  }

  function navigateHome() {
    if (!shouldCommitLocation("/", {})) {
      return;
    }
    setLocation("/", {});
    deps.commitUrl({ to: "/", search: {} });
  }

  function navigateToWorkspace(
    tab: WorkspaceTab,
    options?: { replace?: boolean; currentPath?: string; searchParams?: TabSearchParams }
  ) {
    const nextPath = options?.currentPath ?? tab.currentPath;
    const nextSearchParams = options?.searchParams ?? tab.searchParams;
    const sanitizedSearch = sanitizeWorkspaceSearchParams(tab.kind, nextSearchParams).searchParams;
    const to = buildWorkspacePath({
      ...tab,
      currentPath: nextPath,
    });
    if (!shouldCommitLocation(to, sanitizedSearch)) {
      return;
    }
    setLocation(to, sanitizedSearch);
    deps.commitUrl({
      to,
      search: sanitizedSearch,
      replace: options?.replace,
    });
  }

  function getActiveTabId(): string | null {
    return matchWorkspaceLocation(pathname)?.tabId ?? null;
  }

  function closeTabWithoutGuard(id: string): CloseTabResult {
    guards.delete(id);
    const activeTabId = getActiveTabId();
    const result = closeTabInState(tabs, id, activeTabId);
    if (result.outcome !== "closed") {
      return result.outcome;
    }

    setTabs(result.tabs);

    if (activeTabId === id) {
      if (!result.nextActiveTab) {
        navigateHome();
      } else {
        navigateToWorkspace(result.nextActiveTab);
      }
    }

    return result.outcome;
  }

  /**
   * Adopt the store's current `pathname`/`search` into the tab set: point an existing tab at
   * the requested sub-path, or open a fresh tab for it. The URL is never committed — it is
   * already the location being adopted.
   *
   * `commitTabs` is injected because this runs in two phases with different plumbing. At
   * construction it must assign `tabs` and persist directly: `setTabs` is defined but the
   * store has no subscribers yet and `snapshot` is recomputed right after, so going through
   * it would be an emit into the void. After construction (org switch with
   * `keepCurrentLocation`) it must go through `setTabs` so subscribers actually re-render.
   */
  function adoptCurrentLocation(commitTabs: (nextTabs: WorkspaceTab[]) => void) {
    const workspaceMatch = matchWorkspaceLocation(pathname);
    if (!workspaceMatch) {
      return;
    }

    const { searchParams } = sanitizeWorkspaceSearchParams(workspaceMatch.kind, search);

    const existingTab = tabs.find((tab) => tab.tabId === workspaceMatch.tabId);
    if (existingTab) {
      // The URL is the source of truth here — on boot, and again when an organization
      // switch keeps the location. A stored tab that last sat on another
      // sub-path (a Claudia thread, say) must adopt the requested location here, before
      // the first render: ProjectLayout replays the tab's `currentPath`/`searchParams`
      // through the navigation policy on mount, and stale values there out-voted a deep
      // link into `/ai-assistant/history` (uat-e2e 32200723730). Search params merge the
      // way the policy merges them — the tab's persistent UI state first, the requested
      // query over it — so the replay and the bridge's first `handleUrlChange` see the
      // same location snapshot. Memory is the bridge's to reconcile under the policy.
      const adoptedSearchParams = mergeTabSearchParams(existingTab.searchParams, searchParams);
      if (
        existingTab.currentPath !== workspaceMatch.currentPath ||
        !areSearchParamsEqual(existingTab.searchParams, adoptedSearchParams)
      ) {
        commitTabs(
          tabs.map((tab) =>
            tab.tabId === existingTab.tabId
              ? {
                  ...tab,
                  currentPath: workspaceMatch.currentPath,
                  searchParams: adoptedSearchParams,
                }
              : tab
          )
        );
      }
      return;
    }

    const createdTab =
      workspaceMatch.kind === "project"
        ? createProjectWorkspaceTab({
            resourceId: workspaceMatch.resourceId ?? workspaceMatch.tabId,
            name: workspaceMatch.resourceId ?? workspaceMatch.tabId,
            currentPath: workspaceMatch.currentPath,
            searchParams,
          })
        : createAdminWorkspaceTab({
            currentPath: workspaceMatch.currentPath,
            searchParams,
          });

    const result = openTabInState(tabs, createdTab);
    if (result.created) {
      commitTabs(result.tabs);
    }
  }

  adoptCurrentLocation((nextTabs) => {
    tabs = nextTabs;
    deps.saveTabs(tabs);
  });
  snapshot = computeSnapshot();

  return {
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getSnapshot: () => snapshot,
    openProjectTab(project: { id: string; name: string }) {
      const activeWorkspaceMatch = matchWorkspaceLocation(pathname);
      const tabId = makeProjectTabId(project.id);
      const isOnProjectRoute =
        activeWorkspaceMatch?.kind === "project" && activeWorkspaceMatch.resourceId === project.id;
      const existingTab = tabs.find((tab) => tab.tabId === tabId);
      const nextTab =
        existingTab ??
        createProjectWorkspaceTab({
          resourceId: project.id,
          name: project.name,
          currentPath: isOnProjectRoute ? activeWorkspaceMatch.currentPath : "/",
          searchParams: isOnProjectRoute ? search : {},
        });

      setTabs(openTabInState(tabs, nextTab).tabs);

      if (!isOnProjectRoute) {
        navigateToWorkspace(nextTab);
      }
    },
    openAdminTab() {
      const activeWorkspaceMatch = matchWorkspaceLocation(pathname);
      const isOnAdminRoute = activeWorkspaceMatch?.kind === "admin";
      const existingTab = tabs.find((tab) => tab.tabId === "admin");
      const nextTab =
        existingTab ??
        createAdminWorkspaceTab({
          currentPath: isOnAdminRoute ? activeWorkspaceMatch.currentPath : undefined,
          searchParams: isOnAdminRoute ? search : {},
        });

      setTabs(openTabInState(tabs, nextTab).tabs);

      if (!isOnAdminRoute || pathname !== buildWorkspacePath(nextTab)) {
        navigateToWorkspace(nextTab);
      }
    },
    async closeTab(id: string): Promise<CloseTabResult> {
      const tabExists = tabs.some((tab) => tab.tabId === id);
      if (!tabExists) return "not-found";

      const guard = guards.get(id);
      if (!guard) return closeTabWithoutGuard(id);

      return await new Promise<CloseTabResult>((resolve) => {
        guard({
          type: "close-tab",
          proceed: () => resolve(closeTabWithoutGuard(id)),
          cancel: () => resolve("cancelled"),
        });
      });
    },
    forceCloseTab: closeTabWithoutGuard,
    renameTab(tabId: string, name: string) {
      let updated = false;
      const nextTabs = tabs.map((tab): WorkspaceTab => {
        if (tab.kind !== "project" || tab.tabId !== tabId || tab.name === name) {
          return tab;
        }
        updated = true;
        return { ...tab, name };
      });

      if (updated) {
        setTabs(nextTabs);
      }
    },
    setActiveTab(id: string) {
      const tab = tabs.find((item) => item.tabId === id);
      if (!tab) {
        return;
      }

      const activeTabId = getActiveTabId();
      if (activeTabId && activeTabId !== id) {
        const guard = guards.get(activeTabId);
        if (guard) {
          guard({
            type: "navigate",
            proceed: () => navigateToWorkspace(tab),
            cancel: () => {},
          });
          return;
        }
      }

      navigateToWorkspace(tab);
    },
    goHome: navigateHome,
    isTabOpen(id: string) {
      return tabs.some((tab) => tab.tabId === id);
    },
    navigateTab(
      tabId: string,
      path: string,
      params?: TabSearchParams,
      options?: GuardedTabNavigationOptions
    ) {
      const currentTab = tabs.find((tab) => tab.tabId === tabId);
      if (!currentTab) {
        return;
      }

      const requestedPath = normalizeTabPath(path);
      const policyInput: TabNavigationPolicyInput = {
        path: requestedPath,
        currentSearchParams: sanitizeWorkspaceSearchParams(currentTab.kind, currentTab.searchParams)
          .searchParams,
        requestedSearchParams: params,
        memory: currentTab.memory,
      };
      const policyResult =
        policies.get(tabId)?.(policyInput) ??
        applyDefaultNavigationPolicy(currentTab.kind, policyInput);
      const normalizedPath = normalizeTabPath(policyResult.path);
      const resolvedSearchParams = sanitizeWorkspaceSearchParams(
        currentTab.kind,
        policyResult.searchParams
      ).searchParams;

      const applyNavigation = () => {
        const pathUpdatedTabs = updateTabPathInState(tabs, tabId, normalizedPath).tabs;
        const nextTabs = replaceTabSearchParamsInState(
          pathUpdatedTabs,
          tabId,
          resolvedSearchParams
        ).tabs;
        const memoryUpdatedTabs = updateTabMemoryInState(nextTabs, tabId, policyResult.memory).tabs;
        setTabs(memoryUpdatedTabs);

        if (tabId === getActiveTabId()) {
          const updatedTab = memoryUpdatedTabs.find((tab) => tab.tabId === tabId) ?? currentTab;
          navigateToWorkspace(updatedTab, {
            currentPath: normalizedPath,
            searchParams: resolvedSearchParams,
            replace: options?.replace,
          });
        }
      };

      const guard = guards.get(tabId);
      if (!options?.skipGuard && guard && currentTab.currentPath !== normalizedPath) {
        guard({
          type: "navigate",
          proceed: applyNavigation,
          cancel: () => {},
        });
        return;
      }

      applyNavigation();
    },
    updateTabSearchParams(tabId: string, params: TabSearchParams, options?: TabNavigationOptions) {
      const nextTabs = updateTabSearchParamsInState(tabs, tabId, params).tabs;
      setTabs(nextTabs);

      if (tabId === getActiveTabId()) {
        const tab = nextTabs.find((item) => item.tabId === tabId);
        if (!tab) {
          return;
        }

        navigateToWorkspace(tab, {
          currentPath: tab.currentPath,
          searchParams: tab.searchParams,
          replace: options?.replace,
        });
      }
    },
    updateTabMemory(tabId: string, updates: Partial<TabMemory>) {
      const tab = tabs.find((item) => item.tabId === tabId);
      if (!tab) {
        return;
      }

      const nextTabs = updateTabMemoryInState(
        tabs,
        tabId,
        mergeTabMemory(tab.memory, updates)
      ).tabs;
      setTabs(nextTabs);
    },
    getTab(tabId: string) {
      return tabs.find((tab) => tab.tabId === tabId);
    },
    registerNavigationGuard(tabId: string, guard: NavigationGuardCallback) {
      guards.set(tabId, guard);
    },
    unregisterNavigationGuard(tabId: string) {
      guards.delete(tabId);
    },
    registerNavigationPolicy(tabId: string, policy: TabNavigationPolicy) {
      policies.set(tabId, policy);
    },
    unregisterNavigationPolicy(tabId: string) {
      policies.delete(tabId);
    },
    handleUrlChange(nextPathname: string, nextSearch: TabSearchParams) {
      const locationChanged =
        pathname !== nextPathname || !areSearchParamsEqual(search, nextSearch);
      const activeWorkspaceMatch = matchWorkspaceLocation(nextPathname);
      const nextSearchSanitization = activeWorkspaceMatch
        ? sanitizeWorkspaceSearchParams(activeWorkspaceMatch.kind, nextSearch)
        : { searchParams: nextSearch, changed: false };
      const sanitizedNextSearch = nextSearchSanitization.searchParams;

      if (!locationChanged && activeWorkspaceMatch && !nextSearchSanitization.changed) {
        const activeTab = tabs.find((tab) => tab.tabId === activeWorkspaceMatch.tabId);
        if (
          activeTab &&
          activeTab.currentPath === activeWorkspaceMatch.currentPath &&
          areSearchParamsEqual(activeTab.searchParams, sanitizedNextSearch)
        ) {
          return;
        }
      }

      pathname = nextPathname;
      search = nextSearch;

      if (!activeWorkspaceMatch) {
        if (locationChanged) emit();
        return;
      }

      const { searchParams, changed } = sanitizeWorkspaceSearchParams(
        activeWorkspaceMatch.kind,
        search
      );

      const activeTab = tabs.find((tab) => tab.tabId === activeWorkspaceMatch.tabId);
      if (!activeTab) {
        const createdTab =
          activeWorkspaceMatch.kind === "project"
            ? createProjectWorkspaceTab({
                resourceId: activeWorkspaceMatch.resourceId ?? activeWorkspaceMatch.tabId,
                name: activeWorkspaceMatch.resourceId ?? activeWorkspaceMatch.tabId,
                currentPath: activeWorkspaceMatch.currentPath,
                searchParams,
              })
            : createAdminWorkspaceTab({
                currentPath: activeWorkspaceMatch.currentPath,
                searchParams,
              });

        setTabs(openTabInState(tabs, createdTab).tabs);
        if (changed) {
          navigateToWorkspace(createdTab, {
            replace: true,
            currentPath: activeWorkspaceMatch.currentPath,
            searchParams,
          });
        }
        return;
      }

      const policyResult = policies.get(activeWorkspaceMatch.tabId)?.({
        path: activeWorkspaceMatch.currentPath,
        currentSearchParams: sanitizeWorkspaceSearchParams(activeTab.kind, activeTab.searchParams)
          .searchParams,
        requestedSearchParams: searchParams,
        memory: activeTab.memory,
      });
      const resolvedPath = normalizeTabPath(policyResult?.path ?? activeWorkspaceMatch.currentPath);
      const resolvedSearchParams = sanitizeWorkspaceSearchParams(
        activeTab.kind,
        policyResult?.searchParams ?? searchParams
      ).searchParams;
      const resolvedMemory = policyResult?.memory ?? activeTab.memory;
      const resolvedUrlChanged =
        resolvedPath !== activeWorkspaceMatch.currentPath ||
        !areSearchParamsEqual(resolvedSearchParams, searchParams);

      let updated = false;
      const nextTabs = tabs.map((tab) => {
        if (tab.tabId !== activeWorkspaceMatch.tabId) {
          return tab;
        }

        if (
          tab.currentPath === resolvedPath &&
          areSearchParamsEqual(tab.searchParams, resolvedSearchParams) &&
          areTabMemoryEqual(tab.memory, resolvedMemory)
        ) {
          return tab;
        }

        updated = true;
        return {
          ...tab,
          currentPath: resolvedPath,
          searchParams: resolvedSearchParams,
          memory: resolvedMemory,
        };
      });
      const resolvedTab = nextTabs.find((tab) => tab.tabId === activeWorkspaceMatch.tabId);

      if (updated) {
        setTabs(nextTabs);
      }

      if (changed || resolvedUrlChanged) {
        navigateToWorkspace(resolvedTab ?? activeTab, {
          replace: true,
          currentPath: resolvedPath,
          searchParams: resolvedSearchParams,
        });
        if (locationChanged) emit();
        return;
      }

      if (!updated && locationChanged) {
        emit();
      }
    },
    /**
     * Tabs belong to one organization, so a real switch always clears them.
     *
     * `keepCurrentLocation` (LAT-627) additionally re-adopts the current workspace URL into a
     * fresh tab instead of navigating home: the org-aware project-not-found page switches org
     * precisely *because* the URL already names the project to open, so dropping the user on
     * the projects home would throw away the deep link they clicked. No URL is committed —
     * `pathname`/`search` are unchanged. Off a workspace route (home, settings) there is
     * nothing to re-adopt and the default home navigation applies.
     */
    handleOrganizationChange(
      nextOrganizationId: string | null,
      options?: { keepCurrentLocation?: boolean }
    ) {
      if (
        organizationId !== null &&
        nextOrganizationId !== null &&
        organizationId !== nextOrganizationId
      ) {
        setTabs([]);
        if (options?.keepCurrentLocation && matchWorkspaceLocation(pathname)) {
          adoptCurrentLocation(setTabs);
        } else {
          navigateHome();
        }
      }
      organizationId = nextOrganizationId;
    },
  };
}
